import { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

const LIVE_SLUG = 'ai-bootcamp';
const SELF_PACED_SLUG = 'ai-bootcamp-self-paced';

/**
 * NO FALLBACK PRICES. EVER.
 *
 * Prices and promotions change (sales, early-bird windows, plan changes). A
 * hardcoded fallback would keep rendering a stale price after a change and
 * could show a buyer an amount that does not match what Stripe charges.
 *
 * If the catalog cannot be read, this hook reports an error and the page shows
 * that pricing is unavailable. It never invents a number.
 */

const centsToDisplay = (cents) => {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return null;
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
};

const normalizePlan = (plan) => {
  const installments = plan.metadata?.installments || plan.installments || 1;
  const effective = plan.discountedAmount ?? plan.amount;
  const hasDiscount =
    plan.discountedAmount !== null &&
    plan.discountedAmount !== undefined &&
    plan.discountedAmount !== plan.amount;
  const totalAmount = effective * installments;
  const originalTotalAmount = plan.amount * installments;
  const savings = originalTotalAmount - totalAmount;

  return {
    planKey: plan.planKey,
    label: plan.label,
    installments,
    amount: effective,
    originalAmount: plan.amount,
    hasDiscount,
    totalAmount,
    originalTotalAmount,
    savingsAmount: savings > 0 ? savings : 0,
    display: centsToDisplay(effective),
    totalDisplay: centsToDisplay(totalAmount),
    originalDisplay: centsToDisplay(plan.amount),
    originalTotalDisplay: centsToDisplay(originalTotalAmount),
    savingsDisplay: savings > 0 ? centsToDisplay(savings) : null,
  };
};

/**
 * Single source of truth for the course sales surfaces: pricing, the active
 * tuition window, the upcoming cohort, and its sessions. Every displayed
 * number and date comes from the backend; nothing is hardcoded here.
 */
const useCourseCatalog = ({ courseId } = {}) => {
  const [plans, setPlans] = useState([]);
  const [selfPaced, setSelfPaced] = useState(null);
  const [activePromotion, setActivePromotion] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [cohortName, setCohortName] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pricingError, setPricingError] = useState(null);

  const resolvedCourseId =
    courseId || process.env.REACT_APP_AI_BOT_BUILDER_BOOTCAMP_COURSE_ID;

  useEffect(() => {
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';

    const loadCatalog = async () => {
      try {
        const res = await fetch(`${API_URL}/checkout/catalog`);
        if (!res.ok) throw new Error(`catalog ${res.status}`);
        const data = await res.json();

        const live = data.products?.find((p) => p.slug === LIVE_SLUG);
        const sp = data.products?.find((p) => p.slug === SELF_PACED_SLUG);
        if (!mounted) return;

        // Show every purchasable plan the catalog returns, including the
        // five-payment option. Buyers choose; we do not hide options.
        const visible = (live?.plans || [])
          .filter((p) => p.stripePriceId)
          .map(normalizePlan)
          .sort((a, b) => a.installments - b.installments);

        if (!visible.length) {
          throw new Error('catalog returned no purchasable plans');
        }

        setPlans(visible);
        setSelfPaced(sp?.plans?.length ? normalizePlan(sp.plans[0]) : null);
        if (data.activePromotion) setActivePromotion(data.activePromotion);
        setPricingError(null);
      } catch (err) {
        if (!mounted) return;
        // Never invent a price. Report the failure and let the page say so.
        console.error('[useCourseCatalog] Could not load pricing from the catalog', err);
        setPlans([]);
        setSelfPaced(null);
        setPricingError(err instanceof Error ? err : new Error('pricing_unavailable'));
      }
    };

    const loadSessions = async () => {
      if (!resolvedCourseId) return;
      try {
        const res = await fetch(`${API_URL}/courses/${resolvedCourseId}/bootcamp-data`);
        if (!res.ok) throw new Error(`sessions ${res.status}`);
        const data = await res.json();
        if (!mounted) return;

        const group =
          data.sessionGroups?.future?.[0] ||
          data.sessionGroups?.current ||
          null;
        if (group) {
          setCohortName(group.name || null);
          setSessions(
            (group.sessions || [])
              .filter((s) => s.scheduledAt)
              .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
          );
        }
      } catch (err) {
        /* evergreen mode: page renders without dates */
      }
    };

    Promise.all([loadCatalog(), loadSessions()]).finally(() => {
      if (mounted) setIsLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [resolvedCourseId]);

  const upfrontPlan = useMemo(
    () => plans.find((p) => p.installments === 1) || plans[0] || null,
    [plans]
  );
  const installmentPlan = useMemo(
    () => plans.find((p) => p.installments > 1) || null,
    [plans]
  );

  // A cohort is enrollable only when the backend actually has future sessions.
  const cohortStart = sessions.length ? new Date(sessions[0].scheduledAt) : null;
  const isEnrollable = Boolean(cohortStart && cohortStart.getTime() > Date.now());

  // A promotion only counts as live when the backend gave us a future end date.
  const promoEndsAt = useMemo(() => {
    const raw = activePromotion?.endDate || activePromotion?.end_date;
    if (!raw) return null;
    const d = new Date(raw);
    return !Number.isNaN(d.getTime()) && d.getTime() > Date.now() ? d : null;
  }, [activePromotion]);

  const promoName = activePromotion?.description || activePromotion?.code || null;
  const hasActivePromo = Boolean(promoEndsAt && plans.some((p) => p.hasDiscount));
  const enrollmentDeadline = promoEndsAt;

  const formatSessionDate = useCallback(
    (value) =>
      new Date(value).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: 'America/New_York',
      }),
    []
  );

  return {
    isLoading,
    pricingError,
    plans,
    upfrontPlan,
    installmentPlan,
    selfPaced,
    activePromotion,
    promoEndsAt,
    promoName,
    hasActivePromo,
    enrollmentDeadline,
    sessions,
    cohortName,
    cohortStart,
    isEnrollable,
    formatSessionDate,
  };
};

export default useCourseCatalog;
